import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

interface LocationState {
  message?: string;
}

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [localError, setLocalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const { login, isLoading, isAuthenticated, isAdmin, error: authError, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Verificăm dacă există un mesaj de succes în state-ul de navigare
  useEffect(() => {
    const state = location.state as LocationState;
    if (state?.message) {
      setSuccessMessage(state.message);
    }
  }, [location.state]);

  // Sincronizăm erorile din context cu starea locală
  useEffect(() => {
    if (authError) {
      setLocalError(authError);
    }
  }, [authError]);

  // Redirecționăm dacă utilizatorul este deja autentificat
  useEffect(() => {
    if (isAuthenticated) {
      navigate(isAdmin ? '/admin/dashboard' : '/', { replace: true });
    }
  }, [isAuthenticated, isAdmin, navigate]);

  // Ștergem erorile la dezactivarea componentei
  useEffect(() => {
    return () => {
      clearError(); // Curățăm erorile din context când componenta este dezmontată
    };
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validări de bază
    if (!email.trim() || !password.trim()) {
      setLocalError('Toate câmpurile sunt obligatorii');
      return;
    }
    
    try {
      setLocalError('');
      clearError(); // Curățăm orice eroare anterioară
      
      console.log('Încercare de autentificare cu:', email);
      await login(email, password);
      
      // Navigarea se face automat prin effect după actualizarea stării isAuthenticated
    } catch (err: any) {
      console.error('Eroare la login în pagină:', err);
      setLocalError(err.message || 'A apărut o eroare. Încearcă din nou.');
    }
  };

  // Afișăm orice eroare (locală sau din context)
  const displayError = localError || authError;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Spațiu pentru header-ul fix */}
      <div className="pt-24 md:pt-28"></div>
      
      <main className="flex-grow flex items-start justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">Autentificare</h1>
            <p className="mt-2 text-gray-600">
              Intră în contul tău pentru a accesa biblioteca digitală
            </p>
          </div>
          
          <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            {displayError && (
              <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 text-red-700">
                <p className="font-medium">Eroare la autentificare:</p>
                <p>{displayError}</p>
              </div>
            )}
            
            {successMessage && (
              <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4 text-green-700">
                <p>{successMessage}</p>
              </div>
            )}
            
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Adresa de email
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="nume@exemplu.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Parola
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    Ține-mă minte
                  </label>
                </div>

                <div className="text-sm">
                  <Link to="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500">
                    Ai uitat parola?
                  </Link>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    isLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isLoading ? 'Se procesează...' : 'Autentificare'}
                </button>
              </div>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    sau
                  </span>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  Nu ai un cont?{' '}
                  <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                    Înregistrează-te acum
                  </Link>
                </p>
              </div>
            </div>
          </div>
          
          {/* Credențiale de test - pentru demonstrație */}
          <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-200 mt-6">
            <h3 className="text-md font-medium text-blue-800 mb-2">Conturi de test disponibile:</h3>
            <div className="space-y-2 text-sm">
              <div className="bg-white p-3 rounded border border-blue-100">
                <p className="font-semibold text-gray-700">Admin:</p>
                <p>Email: admin@biblioteca.ro</p>
                <p>Parolă: Parola123!</p>
              </div>
              <div className="bg-white p-3 rounded border border-blue-100">
                <p className="font-semibold text-gray-700">Utilizator obișnuit:</p>
                <p>Email: utilizator@biblioteca.ro</p>
                <p>Parolă: Parola123!</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default LoginPage; 
